import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddOn,
  AddOnCreatedEnvelope,
  AddOnDeletedEnvelope,
  AddOnQuery,
  AddOnUpdatedEnvelope,
  AddOnsQueryBuilder,
  BulkCreateAddOnsOptions,
  BulkCreateAddOnsResponse,
  BulkDeleteAddOnsResponse,
  BulkUpdateAddOnsOptions,
  BulkUpdateAddOnsResponse,
  MaskedAddOn,
  QueryAddOnsResponse,
  UpdateAddOn,
  bulkCreateAddOns as universalBulkCreateAddOns,
  bulkDeleteAddOns as universalBulkDeleteAddOns,
  bulkUpdateAddOns as universalBulkUpdateAddOns,
  createAddOn as universalCreateAddOn,
  deleteAddOn as universalDeleteAddOn,
  getAddOn as universalGetAddOn,
  queryAddOns as universalQueryAddOns,
  typedQueryAddOns as universalTypedQueryAddOns,
  updateAddOn as universalUpdateAddOn,
} from './addons-v1-add-on-add-ons.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function createAddOn(httpClient: HttpClient): CreateAddOnSignature {
  return (addOn: AddOn) =>
    universalCreateAddOn(
      addOn,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAddOnSignature {
  /**
   * Creates an add-on.
   * @param - Add-on to create.
   * @returns Created add-on.
   */
  (addOn: AddOn): Promise<
    NonNullablePaths<
      AddOn,
      | `durationInMinutes`
      | `maxQuantity`
      | `price.value`
      | `price.currency`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function getAddOn(httpClient: HttpClient): GetAddOnSignature {
  return (addOnId: string) =>
    universalGetAddOn(
      addOnId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAddOnSignature {
  /**
   * Retrieves an add-on.
   * @param - ID of the add-on to retrieve.
   * @returns Retrieved add-on.
   */
  (addOnId: string): Promise<
    NonNullablePaths<
      AddOn,
      | `durationInMinutes`
      | `maxQuantity`
      | `price.value`
      | `price.currency`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function updateAddOn(httpClient: HttpClient): UpdateAddOnSignature {
  return (_id: string, addOn: NonNullablePaths<UpdateAddOn, `revision`, 2>) =>
    universalUpdateAddOn(
      _id,
      addOn,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateAddOnSignature {
  /**
   * Updates an existing add-on.
   *
   * Each time an add-on is updated, its revision number increments by 1.
   * The current revision must be passed when updating to ensure you're working with the latest version and prevent unintended overwrites.
   * @param - Add-on ID.
   * @returns Updated add-on.
   */
  (_id: string, addOn: NonNullablePaths<UpdateAddOn, `revision`, 2>): Promise<
    NonNullablePaths<
      AddOn,
      | `durationInMinutes`
      | `maxQuantity`
      | `price.value`
      | `price.currency`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteAddOn(httpClient: HttpClient): DeleteAddOnSignature {
  return (addOnId: string) =>
    universalDeleteAddOn(
      addOnId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAddOnSignature {
  /**
   * Deletes an add-on and removes it from all add-on groups.
   * @param - ID of the add-on to delete.
   */
  (addOnId: string): Promise<void>;
}

export function queryAddOns(httpClient: HttpClient): QueryAddOnsSignature {
  return () =>
    universalQueryAddOns(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAddOnsSignature {
  /**
   * Retrieves up to 100 add-ons, given the specified filtering, sorting, and paging.
   *
   * By default, results are sorted by `id` in ascending order.
   */
  (): AddOnsQueryBuilder;
}

export function typedQueryAddOns(
  httpClient: HttpClient
): TypedQueryAddOnsSignature {
  return (query: AddOnQuery) =>
    universalTypedQueryAddOns(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAddOnsSignature {
  /** */
  (query: AddOnQuery): Promise<
    NonNullablePaths<
      QueryAddOnsResponse,
      | `addOns`
      | `addOns.${number}.durationInMinutes`
      | `addOns.${number}.maxQuantity`
      | `addOns.${number}.price.value`
      | `addOns.${number}.price.currency`,
      5
    >
  >;
}

export function bulkCreateAddOns(
  httpClient: HttpClient
): BulkCreateAddOnsSignature {
  return (addOns: AddOn[], options?: BulkCreateAddOnsOptions) =>
    universalBulkCreateAddOns(
      addOns,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateAddOnsSignature {
  /**
   * Creates multiple add-ons in a single request.
   * @param - List of add-ons to create.
   */
  (addOns: AddOn[], options?: BulkCreateAddOnsOptions): Promise<
    NonNullablePaths<
      BulkCreateAddOnsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.durationInMinutes`
      | `results.${number}.item.maxQuantity`
      | `results.${number}.item.price.value`
      | `results.${number}.item.price.currency`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateAddOns(
  httpClient: HttpClient
): BulkUpdateAddOnsSignature {
  return (
    addOns: NonNullablePaths<MaskedAddOn, `addOn._id` | `addOn.revision`, 3>[],
    options?: BulkUpdateAddOnsOptions
  ) =>
    universalBulkUpdateAddOns(
      addOns,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAddOnsSignature {
  /**
   * Updates multiple add-ons in a single request.
   * @param - List of add-ons to update.
   */
  (
    addOns: NonNullablePaths<MaskedAddOn, `addOn._id` | `addOn.revision`, 3>[],
    options?: BulkUpdateAddOnsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAddOnsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.durationInMinutes`
      | `results.${number}.item.maxQuantity`
      | `results.${number}.item.price.value`
      | `results.${number}.item.price.currency`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteAddOns(
  httpClient: HttpClient
): BulkDeleteAddOnsSignature {
  return (addOnIds: string[]) =>
    universalBulkDeleteAddOns(
      addOnIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteAddOnsSignature {
  /**
   * Deletes multiple add-ons in a single request.
   * @param - IDs of add-ons to delete.
   */
  (addOnIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteAddOnsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onAddOnCreated = EventDefinition(
  'wix.addons.v1.add_on_created',
  true,
  (event: AddOnCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AddOnCreatedEnvelope>();
export const onAddOnDeleted = EventDefinition(
  'wix.addons.v1.add_on_deleted',
  true,
  (event: AddOnDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AddOnDeletedEnvelope>();
export const onAddOnUpdated = EventDefinition(
  'wix.addons.v1.add_on_updated',
  true,
  (event: AddOnUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<AddOnUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddOn,
  AddOnAddOnInfoOneOf,
  AddOnCreatedEnvelope,
  AddOnDeletedEnvelope,
  AddOnQuerySpec,
  AddOnUpdatedEnvelope,
  AddOnsQueryBuilder,
  AddOnsQueryResult,
  Address,
  AddressHint,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkAddOnResult,
  BulkCreateAddOnsOptions,
  BulkCreateAddOnsRequest,
  BulkCreateAddOnsResponse,
  BulkDeleteAddOnsRequest,
  BulkDeleteAddOnsResponse,
  BulkDeleteAddOnsResponseBulkAddOnResult,
  BulkUpdateAddOnTagsByFilterRequest,
  BulkUpdateAddOnTagsByFilterResponse,
  BulkUpdateAddOnTagsRequest,
  BulkUpdateAddOnTagsResponse,
  BulkUpdateAddOnTagsResult,
  BulkUpdateAddOnsOptions,
  BulkUpdateAddOnsRequest,
  BulkUpdateAddOnsResponse,
  BulkUpdateAddOnsResponseBulkAddOnResult,
  BusinessSchedule,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  ConsentPolicy,
  CreateAddOnRequest,
  CreateAddOnResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DayOfWeek,
  DeleteAddOnRequest,
  DeleteAddOnResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GeoCoordinates,
  GetAddOnRequest,
  GetAddOnResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  Locale,
  MaskedAddOn,
  MessageEnvelope,
  Money,
  Multilingual,
  PlacementType,
  Properties,
  PropertiesChange,
  QueryAddOnsRequest,
  QueryAddOnsResponse,
  QueryLocalizedAddOnsRequest,
  QueryLocalizedAddOnsResponse,
  ResolutionMethod,
  RestoreInfo,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  SupportedLanguage,
  TagList,
  Tags,
  TimePeriod,
  Translation,
  UpdateAddOn,
  UpdateAddOnRequest,
  UpdateAddOnResponse,
  WebhookIdentityType,
  utils,
} from './addons-v1-add-on-add-ons.universal.js';
